/* eslint-disable object-curly-spacing */ 
/* eslint-disable no-undef */ 
/* eslint-disable own-handles */ 
/* eslint-disable no-innerhtml-assign */ 
/* eslint-disable consistent-method-names */ 
/* eslint-disable no-jsdoc */ 
/* eslint-disable max-len */
/**
 * Allows to make request using the TomTom's
 * [Structured Geocode API](https://developer.tomtom.com/online-search/online-search-documentation-geocoding/structured-geocode).
 *
 * Parameters can be passed to the constructor or provided via convenient methods that can be chained until
 * the method {{#crossLink "StructuredGeocode/go:method"}}{{/crossLink}} performs the actual call.
 *
 * The call is asynchronous therefore the user have two options for receive the response:
 *  - Passing a callback function.
 *  - Use the {{#crossLink "Promise"}}{{/crossLink}} returned by the
 *    {{#crossLink "StructuredGeocode/go:method"}}{{/crossLink}} method to handle the response.
 *
 * The SDK provides a {{#crossLink "Promise"}}{{/crossLink}} polyfill for browsers (IE<9)
 * without native ECMAScript 6 support.
 *
 * @class StructuredGeocode
 * @uses Chain
 * @module Services
 * @uses KeyMixin
 * @uses LimitMixin
 * @uses OffsetMixin
 * @uses LanguageMixin
 * @uses ProtocolMixin
 * @uses BestResultMixin
 * @constructor
 *
 * @param {Object} [options] Options to be passed to the structured geocoding call,
 *     or Array of such options objects to perform batch request.
 * @param {Function} [options.callback] Callback function to use in case of success
 * @param {Function} [options.fail=default] Callback function to use in case of failure
 * @param {"http"|"https"} [options.protocol="https"] Protocol's type
 * @param {Boolean}  [options.bestResult=false] Return only one result, the best match result
 *
 * @param {String} [options.key] An API Key issued from <a href="http://developer.tomtom.com">Developer Portal</a>
 * @param {String} [options.countryCode] 2 or 3 country code letters (e.g.: FR, ES).
 *     **This option is required** to be defined here or by using the
 *     {{#crossLink "StructuredGeocode/countryCode:method"}}{{/crossLink}} method.
 * @param {Number} [options.limit=10] Maximum number of responses that will be returned
 * @param {Number} [options.offset=0] Starting offset of the returned results within the full result set
 * @param {String} [options.language] Language to use in the response.
 *  The list with all the available codes can be found
 *  <a href="https://developer.tomtom.com/online-search/online-search-documentation/supported-languages">here</a>.
 * @param {String} [options.streetNumber] Street Number
 * @param {String} [options.streetName] Street name
 * @param {String} [options.crossStreet] Cross street name
 * @param {String} [options.municipality] Municipality (city/town)
 * @param {String} [options.municipalitySubdivision] Municipality subdivision (sub/super city)
 * @param {String} [options.countryTertiarySubdivision] Named area
 * @param {String} [options.countrySecondarySubdivision] County
 * @param {String} [options.countrySubdivision] State or province
 * @param {String} [options.postalCode] Zip or postal code
 *
 * @example
 * ```javascript
 * function callbackFn(result) {
 *   console.log(result);
 * };
 * tomtom.structuredGeocode()
 *   .countryCode("GB")
 *   .callback(callbackFn)
 *   .go();
 * ```
 */
/* eslint-enable max-len */
define(['rest/structuredGeocode', 'model/geocode/geocode', './chain', './validators', 'model/geocode/batchGeocode',
    './converters'],
function(structuredGeocode, model, chain, validators, batchModel, converters) {
    'use strict';

    var fields = {
            key: {validator: validators.string},
            /**
             * Sets or returns the value of the `countryCode` option.
             *
             * Represents the country's code.
             *
             * @method countryCode
             * @chainable
             * @param {String} [newValue] 2 or 3 country code letters
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {String} The same service instance or the current option value if no argument was given
             */
            countryCode: {validator: validators.countryCode, required: true},
            limit: {validator: validators.limit},
            offset: {validator: validators.naturalInteger, name: 'ofs'},
            language: {validator: validators.languageCode},

            /**
             * Sets or returns the value of the `streetNumber` option.
             *
             * Represents the street's number for the structured address.
             *
             * @method streetNumber
             * @chainable
             * @param {String} [newValue] Street's number
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {String} The same service instance or the current option value if no argument was given
             */
            streetNumber: {validator: validators.string},

            /**
             * Sets or returns the value of the `streetName` option.
             *
             * Represents the street's name for the structured address.
             *
             * @method streetName
             * @chainable
             * @param {String} [newValue] Street's name
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {String} The same service instance or the current option value if no argument was given
             */
            streetName: {validator: validators.string},

            /**
             * Sets or returns the value of the `crossStreet` option.
             *
             * Represents the cross street's name for the structured address.
             *
             * @method crossStreet
             * @chainable
             * @param {String} [newValue] Cross street's name
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {String} The same service instance or the current option value if no argument was given
             */
            crossStreet: {validator: validators.string},

            /**
             * Sets or returns the value of the `municipality` option.
             *
             * Represents the municipality (city/town) for the structured address.
             *
             * @method municipality
             * @chainable
             * @param {String} [newValue] Municipality's name (city/town)
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {String} The same service instance or the current option value if no argument was given
             */
            municipality: {validator: validators.string},

            /**
             * Sets or returns the value of the `municipalitySubdivision` option.
             *
             * Represents the municipality subdivision (sub/super city) for the structured address.
             *
             * @method municipalitySubdivision
             * @chainable
             * @param {String} [newValue] Municipality subdivision (sub/super city)
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {String} The same service instance or the current option value if no argument was given
             */
            municipalitySubdivision: {validator: validators.string},

            /**
             * Sets or returns the value of the `countryTertiarySubdivision` option.
             *
             * Represents the area's name for the structured address.
             *
             * @method countryTertiarySubdivision
             * @chainable
             * @param {String} [newValue] Name of the area
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {String} The same service instance or the current option value if no argument was given
             */
            countryTertiarySubdivision: {validator: validators.string},

            /**
             * Sets or returns the value of the `countrySecondarySubdivision` option.
             *
             * Represents the county's name for the structured address.
             *
             * @method countrySecondarySubdivision
             * @chainable
             * @param {String} [newValue] County's name
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {String} The same service instance or the current option value if no argument was given
             */
            countrySecondarySubdivision: {validator: validators.string},

            /**
             * Sets or returns the value of the `countrySubdivision` option.
             *
             * Represents the state or province's name for the structured address.
             *
             * @method countrySubdivision
             * @chainable
             * @param {String} [newValue] State or province
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {String} The same service instance or the current option value if no argument was given
             */
            countrySubdivision: {validator: validators.string},

            /**
             * Sets or returns the value of the `postalCode` option.
             *
             * Represents the zip code or postal code for the structured address.
             *
             * @method postalCode
             * @chainable
             * @param {String} [newValue] Zip or postal code
             * @throws {TypeError} If the given argument cannot be converted into a valid value
             * @return {String} The same service instance or the current option value if no argument was given
             */
            postalCode: {validator: validators.string},

            protocol: {validator: validators.oneOfValue(['http', 'https'], 'protocol')},
            bestResult: {validator: validators.bool}
        },
        batchFields = {
            batchItems: {
                validator: validators.arrayOf(fields),
                converter: converters.arrayOf(fields)
            }
        };

    function checkBestResult(data) {
        var result = Boolean(data.bestResult);
        delete data.bestResult;
        return result;
    }

    function modifyRequestParams(data) {
        var bestResult = checkBestResult(data);
        if (bestResult) {
            data.limit = 1;
            data.offset = 0;
        }
    }

    function handleBatchChain(data, callback) {
        data.batchItems.forEach(modifyRequestParams);
        structuredGeocode.batch(data, function(results) {
            callback.call(this, batchModel(results));
        });
    }

    function handleChain(data, callback) {
        var bestResult = Boolean(data.bestResult);
        modifyRequestParams(data);
        structuredGeocode.structuredGeocode(data, function(response) {
            if (bestResult) {
                callback.call(this, model(response)[0]);
            } else {
                callback.call(this, model(response));
            }
        });
    }

    return function(options) {
        if (Array.isArray(options)) {
            return chain(batchFields, handleBatchChain, {
                batchItems: options
            });
        } else {
            return chain(fields, handleChain, options);
        }
    };
});



// WEBPACK FOOTER //
// ./services/structuredGeocode.js